import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import { SupportChatProvider } from "./context/SupportChatContext";
import { SocketProvider } from "./context/SocketContext";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import adminAxios from "./admin/adminAxios"; 

/* ==========================================================
   MARITIME COMPONENT REGISTRY
   ========================================================== */

// Layouts
import MainLayout from "./layouts/MainLayout";
import AdminLayout from "./layouts/AdminLayout";

// Public & Auth Pages
import LandingPage from "./pages/LandingPage";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import PendingApproval from "./pages/PendingApproval";
import NotFound from "./pages/NotFound";

// User Dashboard Pages
import Home from "./pages/Home";
import Products from "./pages/Products";
import Orders from "./pages/Orders";
import Wallet from "./pages/Wallet";
import Profile from "./pages/Profile";
import Chat from "./pages/Chat";
import ChatList from "./pages/ChatList";
import Manual from "./pages/Manual";

// Admin Command Pages
import AdminDashboard from "./admin/AdminDashboard";
import ManageUsers from "./admin/ManageUsers";
import ManageOrders from "./admin/ManageOrders";
import AssignProducts from "./admin/AssignProducts";
import AdminBanners from "./components/AdminBanners"; 
import WithdrawRequests from "./admin/WithdrawRequests";
import AdminChat from "./admin/AdminChat";
import AdminAddProduct from "./admin/AdminAddProduct";
import PaymentAudit from "./admin/PaymentAudit";
import FinanceManagement from './admin/FinanceManagement';
import AdminProtectedRoute from './components/AdminProtectedRoute';
import ManageAutoReplies from './admin/ManageAutoReplies';

// Super Admin Pages
import SuperAdminLogin from "./admin/SuperAdminLogin";
import SuperAdminDashboard from "./admin/SuperAdminDashboard";
import SuperAdminLayout from "./layouts/SuperAdminLayout";
import CreateAdmin from "./admin/CreateAdmin";
import UserList from "./admin/UserList";
import MaintenancePanel from "./admin/MaintenancePanel"; 
import AdminSettings from "./admin/AdminSettings";       

// Components
import PaymentModal from "./components/PaymentModal"; 

/* ========================================================== */

const AppContent = () => {
  const { user, loading, socket } = useAuth();
  const { colors } = useTheme(); 
  const [isAdminSidebarOpen, setIsAdminSidebarOpen] = useState(true);
  
  // MAINTENANCE STATE
  const [isMaintenance, setIsMaintenance] = useState(false);

  // FETCH MAINTENANCE STATUS
  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const { data } = await adminAxios.get("/settings/maintenance");
        setIsMaintenance(data.isMaintenanceMode);
      } catch (err) {
        console.warn("Maintenance check bypassed.");
      }
    };
    checkMaintenance();

    if (socket) {
      socket.on("maintenanceUpdate", (status) => {
        setIsMaintenance(status);
      });
      return () => socket.off("maintenanceUpdate");
    }
  }, [socket]);

  if (loading) return null;

  // MAINTENANCE BLOCKER UI
  if (isMaintenance && user?.role !== "admin") {
    return (
      <div style={{ 
        height: "100vh", 
        display: "flex", 
        flexDirection: "column",
        alignItems: "center", 
        justifyContent: "center", 
        background: "#060608", 
        color: "#fff",
        textAlign: "center",
        fontFamily: "monospace"
      }}>
        <h1 style={{ color: "#03f8d7", letterSpacing: "5px", fontSize: "2rem" }}>🛠️ SYSTEM LOCKED</h1>
        <p style={{ color: "#64748b", maxWidth: "450px", padding: "0 20px", lineHeight: "1.6" }}>
          MARITIME CORE is currently undergoing scheduled maintenance. 
          All standard accounts are temporarily offline. Please check back later.
          Thank You!
        </p>
        <div style={{ 
          marginTop: "30px", 
          width: "60px", 
          height: "2px", 
          background: "#03f8d7",
          boxShadow: "0 0 15px #03f8d7" 
        }} />
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: "100vh", 
      display: "flex", 
      flexDirection: "column",
      backgroundColor: colors?.bg || "#060608", 
      transition: "background-color 0.3s ease" 
    }}>
      <Routes>
        {/* PUBLIC LANDING - SENSE REDIRECTS */}
        <Route path="/" element={
          !user ? <LandingPage /> : 
          user.role === "admin" ? <Navigate to="/admin/dashboard" replace /> :
          user.verified === false ? <Navigate to="/pending-approval" replace /> :
          <Navigate to="/dashboard" replace />
        } />

        {/* AUTH ENTRANCE */}
        <Route path="/login" element={
          !user ? <Login /> : 
          user.role === "admin" ? <Navigate to="/admin/dashboard" replace /> :
          user.verified === false ? <Navigate to="/pending-approval" replace /> : 
          <Navigate to="/dashboard" replace />
        } />
        
        <Route path="/signup" element={
          !user ? <Signup /> : 
          user.role === "admin" ? <Navigate to="/admin/dashboard" replace /> :
          user.verified === false ? <Navigate to="/pending-approval" replace /> : 
          <Navigate to="/dashboard" replace />
        } />

        {/* USER DASHBOARD ROUTE GROUP */}
        <Route path="/" element={
          user && user.role !== "admin" && user.verified !== false 
          ? <MainLayout><Outlet /></MainLayout> 
          : <Navigate to="/login" replace />
        }>
          <Route path="dashboard" element={<Home />} />
          <Route path="products" element={<Products />} />
          <Route path="orders" element={<Orders />} />
          <Route path="wallet" element={<Wallet />} />
          <Route path="profile" element={<Profile />} />
          <Route path="chat" element={<ChatList />} />
          <Route path="chat/:conversationId" element={<Chat />} />
          <Route path="manual" element={<Manual />} />
          <Route path="pay-test" element={<PaymentModal isOpen={true} onClose={() => window.history.back()} colors={colors} />} />
        </Route>

        {/* SPECIAL STATES */}
        <Route path="/pending-approval" element={<PendingApproval />} />

        {/* MARITIME ADMIN COMMAND CENTER (Tenant Isolated) */}
        <Route
          path="/admin"
          element={
            user?.role === "admin" 
            ? <AdminLayout isOpen={isAdminSidebarOpen} setIsOpen={setIsAdminSidebarOpen}><Outlet /></AdminLayout> 
            : <Navigate to="/login" replace />
          }
        >
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<AdminDashboard isOpen={isAdminSidebarOpen} setIsOpen={setIsAdminSidebarOpen} />} />
          <Route path="users" element={<ManageUsers isOpen={isAdminSidebarOpen} setIsOpen={setIsAdminSidebarOpen} />} />
          <Route path="orders" element={<ManageOrders isOpen={isAdminSidebarOpen} setIsOpen={setIsAdminSidebarOpen} />} />
          <Route path="assign" element={<AssignProducts isOpen={isAdminSidebarOpen} setIsOpen={setIsAdminSidebarOpen} />} />
          <Route path="banners" element={<AdminBanners isOpen={isAdminSidebarOpen} setIsOpen={setIsAdminSidebarOpen} />} />
          <Route path="withdraw" element={<WithdrawRequests isOpen={isAdminSidebarOpen} setIsOpen={setIsAdminSidebarOpen} />} />
          <Route path="chat" element={<AdminChat isOpen={isAdminSidebarOpen} setIsOpen={setIsAdminSidebarOpen} />} />
          <Route path="add-product" element={<AdminAddProduct isOpen={isAdminSidebarOpen} setIsOpen={setIsAdminSidebarOpen} />} />
          <Route path="payment-audit" element={<PaymentAudit isOpen={isAdminSidebarOpen} />} />
          <Route path="finance" element={<AdminProtectedRoute><FinanceManagement /></AdminProtectedRoute>} />
          <Route path="auto-replies" element={<ManageAutoReplies />} />
        </Route>
          
        {/* Superadmin Routes */}
        <Route path="/super-admin/login" element={<SuperAdminLogin />} />
  
        <Route path="/super-admin" element={<SuperAdminLayout />}>
          <Route index element={<SuperAdminDashboard />} />
          <Route path="create-admin" element={<CreateAdmin />} />
          <Route path="users" element={<UserList />} />
          <Route path="maintenance" element={<MaintenancePanel />} />
          <Route path="settings" element={<AdminSettings />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </div>
  );
};

function App() {
  return (
    <SocketProvider>
      <SupportChatProvider>
        <ThemeProvider>
          <BrowserRouter>
            <AppContent />
          </BrowserRouter>
        </ThemeProvider>
      </SupportChatProvider>
    </SocketProvider>
  );
}

export default App;